import { inject, Injectable, OnDestroy } from '@angular/core';
import { Platform, NavController } from '@ionic/angular';
import {
	ModalController, PopoverController, ActionSheetController,
	AlertController, ToastController, MenuController
} from '@ionic/angular/standalone';
import { fromEvent, Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class BackInterceptorService implements OnDestroy {

	private platform = inject(Platform);
	private navCtrl = inject(NavController);
	private modalCtrl = inject(ModalController);
	private popoverCtrl = inject(PopoverController);
	private actionSheetCtrl = inject(ActionSheetController);
	private alertCtrl = inject(AlertController);
	private toastCtrl = inject(ToastController);
	private menuCtrl = inject(MenuController);

	private hwSub?: Subscription;     // back hardware (Capacitor/Android)
	private webSub?: Subscription;    // popstate (browser)
	private overlayOpenCount = 0;     // quante overlay sono aperte
	private overlayWillFns: Array<() => void> = [];
	private overlayDidFns: Array<() => void> = [];

	private priority = 9999;
	private SENTINEL_KEY = '__overlayTrap';

	init(): void {
		// 1) Listener back hardware
		if (!this.hwSub) {
			this.hwSub = this.platform.backButton.subscribeWithPriority(this.priority, async () => {
				if (await this.dismissTopOverlay()) return;  // chiuso overlay -> stop
				this.navCtrl.back();                         // nessuna overlay -> naviga indietro
			});
		}

		// 2) Fallback web: usa sentinelle + popstate
		if (!this.webSub && !this.platform.is('hybrid')) {
			this.webSub = fromEvent<PopStateEvent>(window, 'popstate').subscribe(async (ev) => {
				// se è la nostra sentinella, chiudi overlay e NON navigare
				if ((ev.state && (ev.state as any)[this.SENTINEL_KEY]) || this.overlayOpenCount > 0) {
					// chiude il top overlay (se presente)
					const handled = await this.dismissTopOverlay();
					// se restano overlay aperte, ripiazza la sentinella in cima
					if (this.overlayOpenCount > 0) this.pushSentinel();
					// Importante: niente history.forward() e nessuna nav qui
					return;
				}
				// altrimenti: lascia che il router gestisca il back normalmente
			});
		}

		// 3) Traccia apertura/chiusura overlay per mettere/togliere la sentinella
		this.attachOverlayGlobalListeners();
	}

	destroy(): void {
		this.hwSub?.unsubscribe(); this.hwSub = undefined;
		this.webSub?.unsubscribe(); this.webSub = undefined;
		this.detachOverlayGlobalListeners();
	}

	ngOnDestroy(): void { this.destroy(); }

	// ------------ Sentinella di history ------------
	private pushSentinel(): void {
		try {
			const state = { ...(history.state || {}), [this.SENTINEL_KEY]: true, t: Date.now() };
			history.pushState(state, '', window.location.href); // stesso URL -> niente cambio pagina
		} catch { /* ignore */ }
	}

	private onOverlayWillPresent = () => {
		this.overlayOpenCount++;
		if (this.overlayOpenCount === 1 && !this.platform.is('hybrid')) {
			this.pushSentinel();
		}
	};

	private onOverlayDidDismiss = () => {
		this.overlayOpenCount = Math.max(0, this.overlayOpenCount - 1);
		// non tocchiamo la history qui: la sentinella verrà "consumata" al back
	};

	private attachOverlayGlobalListeners(): void {
		const willEvents = [
			'ionModalWillPresent', 'ionPopoverWillPresent', 'ionActionSheetWillPresent',
			'ionAlertWillPresent', 'ionToastWillPresent'
		];
		const didEvents = [
			'ionModalDidDismiss', 'ionPopoverDidDismiss', 'ionActionSheetDidDismiss',
			'ionAlertDidDismiss', 'ionToastDidDismiss'
		];

		// registra e salva funzioni per detach
		for (const ev of willEvents) {
			const fn = () => this.onOverlayWillPresent();
			document.addEventListener(ev, fn as EventListener, true);
			this.overlayWillFns.push(() => document.removeEventListener(ev, fn as EventListener, true));
		}
		for (const ev of didEvents) {
			const fn = () => this.onOverlayDidDismiss();
			document.addEventListener(ev, fn as EventListener, true);
			this.overlayDidFns.push(() => document.removeEventListener(ev, fn as EventListener, true));
		}
	}

	private detachOverlayGlobalListeners(): void {
		this.overlayWillFns.forEach(off => off()); this.overlayWillFns = [];
		this.overlayDidFns.forEach(off => off()); this.overlayDidFns = [];
	}

	// ------------ Chiusura overlay ------------
	private async dismissTopOverlay(): Promise<boolean> {
		// via controller (creazione programmatica)
		const topModal = await this.modalCtrl.getTop();
		if (topModal) { await topModal.dismiss(); return true; }

		const topActionSheet = await this.actionSheetCtrl.getTop();
		if (topActionSheet) { await topActionSheet.dismiss(); return true; }

		const topPopover = await this.popoverCtrl.getTop();
		if (topPopover) { await topPopover.dismiss(); return true; }

		const topAlert = await this.alertCtrl.getTop();
		if (topAlert) { await topAlert.dismiss(); return true; }

		const topToast = await this.toastCtrl.getTop?.();
		if (topToast) { await topToast.dismiss(); return true; }

		// menu laterale
		const menuOpen = await this.menuCtrl.isOpen();
		if (menuOpen) { await this.menuCtrl.close(); return true; }

		// fallback per overlay dichiarative ([isOpen])
		const openOverlays = Array.from(
			document.querySelectorAll<HTMLElement>('ion-modal,ion-popover,ion-action-sheet,ion-alert,ion-toast')
		).map(el => ({
			el: el as any,
			isOpen: (el as any).isOpen ?? (el as any).presented ?? false,
			z: Number(getComputedStyle(el).zIndex) || 0
		}))
			.filter(x => x.isOpen)
			.sort((a, b) => a.z - b.z);

		const last = openOverlays.length ? openOverlays[openOverlays.length - 1] : undefined;
		const top = last?.el;
		if (top && typeof top.dismiss === 'function') {
			await top.dismiss();
			return true;
		}

		return false;
	}
}
